import { API } from '../constants/constant';
import { clearProfileSession, getDocumentRuntime, loginUrl } from './runtime';
import { describeFailure, now, traceIdFromInit } from './requestFailure';

export function unwrapReply(json) {
  if (json?.status === 'pass') return json.result;
  if (json?.jsonrpc === '2.0' && json.result && !json.error) {
    if (json.result.status === 'pass') return json.result.result;
    if (!json.result.status && Object.prototype.hasOwnProperty.call(json.result, 'data')) return json.result;
  }
  const message = json?.error?.data?.message || json?.message || 'Backend request failed.';
  const error = new Error(message);
  error.accessDenied = /accessdenied|access denied|invalid api key|unauthorized/i.test(message)
    || /AccessDenied/.test(json?.error?.data?.name || '');
  throw error;
}

// `trace` is an optional out-parameter: it records stage/status/trace id so a
// failure can be described afterwards without changing what is thrown.
export async function requestForDocument({ body = {}, url, method = 'POST' }, runtime, fetchImpl = fetch, trace = {}) {
  trace.method = method;
  trace.url = url;
  trace.startedAt = now();
  trace.stage = 'session';
  if (method !== 'POST') throw new Error('Unsupported compatibility request method.');
  const login = url === API.VENDOR_LOGIN;
  if (!login) runtime.assertRequest(url);
  const headers = { 'Content-Type': 'application/json', 'X-Odoo-Database': runtime.profile.database };
  if (!login) headers['API-KEY'] = runtime.requireSession().token;
  const params = login ? { ...body, db: runtime.profile.database } : body;
  const init = {
    method, headers, body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params }),
    credentials: 'omit', redirect: 'error',
  };
  trace.stage = 'fetch';
  const pending = fetchImpl(runtime.profile.apiBaseUrl + url, init);
  trace.traceId = traceIdFromInit(init);
  const response = await pending;
  trace.status = response.status;
  // Account switches/logout in another tab invalidate both future calls and
  // in-flight results. Never publish an old actor's data into a new session.
  trace.stage = 'session';
  if (!login) runtime.requireSession();
  trace.stage = 'parse';
  const json = await response.json();
  trace.stage = 'http';
  if (!response.ok && [401, 498].includes(response.status)) {
    const error = new Error('Backend session expired.');
    error.accessDenied = true;
    throw error;
  }
  if (!response.ok) throw new Error('Backend HTTP request failed.');
  trace.stage = 'session';
  if (!login) runtime.requireSession();
  else runtime.assertLoginUnchanged();
  trace.stage = 'unwrap';
  return unwrapReply(json);
}

export async function fetchForDocument(params, onError) {
  let runtime;
  const trace = {};
  try {
    runtime = getDocumentRuntime();
    return await requestForDocument(params, runtime, undefined, trace);
  } catch (error) {
    if (error.accessDenied && runtime) {
      clearProfileSession(runtime.profile, window.localStorage, runtime.session);
      window.location.assign(loginUrl(runtime));
    } else {
      // Sanitized metadata only (never error text, params, headers or query). It rides on the
      // existing popup-error report; a separate event would trip the frontend-errors alert.
      const request = describeFailure(error, { method: params?.method, url: params?.url, startedAt: now(), ...trace });
      onError(error.message || 'Backend request failed.', { request });
    }
    return undefined;
  }
}

export function openProfileDocument(value, target = '_blank') {
  const runtime = getDocumentRuntime();
  runtime.requireSession();
  if (runtime.profile.readOnly) throw new Error('O19 report/print delivery is not enabled for this workflow.');
  if (typeof value !== 'string') throw new Error('Invalid document URL.');
  const url = new URL(value, runtime.profile.apiBaseUrl + '/');
  // Verified Production web.base.url differs from the Vendor reverse proxy.
  const productionDocumentOrigin = runtime.profile.id === 'odoo17-compat'
    && runtime.profile.apiBaseUrl === 'https://vendor17.shakawear.com/pwa'
    && runtime.profile.database === 'shakawear_production'
    && url.origin === 'https://odoo17.shakawear.com';
  const isOwnHttps = url.protocol === 'https:'
    && (url.origin === new URL(runtime.profile.apiBaseUrl).origin || productionDocumentOrigin);
  const isOwnBlob = url.protocol === 'blob:' && url.origin === window.location.origin;
  const isPdf = /^data:application\/pdf;base64,[A-Za-z0-9+/=\r\n]+$/.test(value);
  if ((!isOwnHttps && !isOwnBlob && !isPdf) || url.username || url.password) {
    throw new Error('Document belongs to another backend or an unsupported URL scheme.');
  }
  return window.open(url.href, target, 'noopener,noreferrer');
}
